// One StoreRowLookups per page, built from the shared data views (each is computed once per data change for every
// component, data/README.md), plus live presence that ticks every minute so "Online" dots go stale on time.

import { useMemo } from 'react';
import { useCustomFields, useRecordLookups, useRepPositions, useSettings } from '@/data/hooks';
import { useTicker } from '@/ui';
import { presenceMap, storeRowLookups, type StoreRowLookups } from './storeRowModel';

/**
 * Lookups for `StoreRow` (and anything else that renders store bits): derived stats, users, pipelines, visit results,
 * the company currency, "show on card" custom fields and presence. The result keeps its identity until one of those
 * changes (or the minute ticks), so pass it straight to memoized rows.
 */
export function useStoreRowLookups(): StoreRowLookups {
  const base = useRecordLookups();
  const settings = useSettings();
  const customFields = useCustomFields();
  const positions = useRepPositions();
  const minute = useTicker(60_000);
  return useMemo(() => {
    void minute; // re-evaluates presence every minute
    return storeRowLookups(base, {
      currencyCode: settings.currencyCode,
      customFields: customFields.values,
      presence: presenceMap(positions, new Date()),
    });
  }, [base, settings.currencyCode, customFields, positions, minute]);
}
